import { StorageSerializers, useLocalStorage } from '@vueuse/core'

export const useAuth = () => {
  const cookieOptions = {
    readonly: false,
    path: '/',
    sameSite: 'lax',
    maxAge: 60 * 60 * 24 * 30, // 30 days
  } as const
  const token = useCookie('_auth', cookieOptions)
  const rfToken = useCookie('_auth_refresh', cookieOptions)

  const user = useLocalStorage<LoginResponse['userInfo'] | null>('user', null, {
    serializer: StorageSerializers.object,
  })

  const isHost = computed(() => !!user.value?.isHost)
  const isGuestUser = computed(() => !!user.value?.isGuest)

  const getToken = () => toValue(token)
  const getRefreshToken = () => toValue(rfToken)

  const isAuthenticated = () => !!getToken()

  const logout = async () => {
    setUser(null)
    await navigateTo('/')
  }

  const updateUser = (partialUser: Partial<LoginResponse['userInfo']>) => {
    user.value = {
      ...user.value,
      bio: partialUser?.bio,
      userImage: partialUser?.userImage,
      userCoverImage: partialUser?.userCoverImage,
    } as LoginResponse['userInfo']
  }

  const setUser = (loginResponse: LoginResponse | null) => {
    if (loginResponse?.userInfo) {
      const fullUser: User = {
        ...loginResponse.userInfo,
        ...{
          deviceUDID: loginResponse?.deviceUDID,
          deviceType: loginResponse?.deviceType,
        },
        isGuest: loginResponse.isGuest,
      }

      user.value = fullUser

      updateToken({
        refreshToken: loginResponse.refreshToken,
        accessToken: loginResponse.accessToken,
      })
    } else {
      user.value = null
      token.value = null
      rfToken.value = null
    }
  }

  const updateToken = (refreshResponse: Partial<LoginResponse>) => {
    token.value = refreshResponse.accessToken
    rfToken.value = refreshResponse.refreshToken
  }

  const handleUserDeactivation = async () => {
    setUser(null)
    if (window.location.pathname === '/') {
      showToastOnce(
        API_ERROR_MESSAGE[ApiStatusCode.ACCOUNT_DEACTIVE],
        ApiStatusCode.ACCOUNT_DEACTIVE,
      )
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } else if (window.location.pathname !== '/login') {
      await navigateTo('/')
      showToastOnce(
        API_ERROR_MESSAGE[ApiStatusCode.ACCOUNT_DEACTIVE],
        ApiStatusCode.ACCOUNT_DEACTIVE,
      )
    }
  }

  return {
    user,
    isHost,
    isGuestUser,
    getToken,
    getRefreshToken,
    setUser,
    updateUser,
    updateToken,
    isAuthenticated,
    logout,
    handleUserDeactivation,
  }
}
